import {Button, Checkbox, Col, Collapse, Divider, Radio, Row, Space} from "antd";
import React, {useEffect, useMemo, useState} from "react";
import {IssueFilterDrawer} from "@components/issues-filter-drawer";
import {fixFilterObjectEmptyArrays, IssueFilterDrawerProps} from "@components/issues-filter-drawer/IssueFilterDrawer";
import {isEqual} from "underscore";
import {IssueFiltersQuery, useGetFilterOptionsQuery} from "@mwServices/Issue";
import {FilterIcon} from "@components/icons/Icons";
import {formatTitleCase} from "@lib/format";
import useSubscriptionType from "@hooks/useSubscriptionType";
import {SCREEN, useWindowSize} from "@components/window-size-provider";

const { Panel } = Collapse;

interface NewReleasesFiltersDrawerProps extends IssueFilterDrawerProps {
    weekDates?: {start: string; end: string;}
}

const NewReleasesFiltersContent = ({ filters, setFilters, weekDates}: NewReleasesFiltersDrawerProps) => {
    const subscriptionType = useSubscriptionType();

    const [queryParams, setQueryParams] = useState<IssueFiltersQuery>({
            filters: {
                hideSeriesFromNewReleases: true,
                date: weekDates,
                showVariants: true,
            }
        });
    const filterOptionsQuery = useGetFilterOptionsQuery(queryParams);

    const {publishers} = useMemo(() => {
        if(filterOptionsQuery.data) return filterOptionsQuery.data;
        return {publishers: []};
    }, [filterOptionsQuery]);

    useEffect(() => {
        setQueryParams({
            filters: {
                hideSeriesFromNewReleases: true,
                date: weekDates,
                showVariants: true,
            }
        });
    }, [weekDates]);

    return (
        <>
            <Row>
                <Col span={24}>
                    <div className="font-18 font-bold" >Sort by:</div>
                    <br/>

                    <Radio.Group
                        className="pad-left-10"
                        onChange={(event) => setFilters({...filters, sort: event.target.value})}
                        value={filters.sort}
                    >
                        <Space direction="vertical">
                            <Radio value={25} >Publishers (Most to Fewest)</Radio>
                            <Radio value={7} >Publisher A-Z</Radio>
                            <Radio value={subscriptionType.isUnlimitedOrTrial ? 1 : null} disabled={!subscriptionType.isUnlimitedOrTrial}>Est Value (Highest to Lowest)</Radio>
                            <Radio value={subscriptionType.isUnlimitedOrTrial ? 2 : null} disabled={!subscriptionType.isUnlimitedOrTrial}>Current Market Trend (%) (Highest to Lowest)</Radio>
                        </Space>
                    </Radio.Group>
                </Col>
            </Row>
            <Divider />

            <Row>
                <Col span={24}>
                    <div className="font-18 font-bold" >Filter by:</div>

                    <Collapse className="filtersAccordion" ghost defaultActiveKey={[1,2,3]} expandIconPosition={"end"}>
                        <Panel header="#1s" key={1}>
                            <Space direction="vertical">
                                <Checkbox checked={!!filters.onlyFirstIssues} onChange={(event) => setFilters({...filters, onlyFirstIssues: event.target.checked})}>{formatTitleCase('Only show #1s')}</Checkbox>
                            </Space>
                        </Panel>
                        <Panel header="Regular / Variants" key={2}>
                            <Space direction="vertical">
                                <Checkbox checked={true} disabled>{formatTitleCase('Regular')}</Checkbox>
                                <Checkbox checked={!!filters.showVariants} onChange={(event) => setFilters({...filters, showVariants: event.target.checked})}>{formatTitleCase('Show Variants')}</Checkbox>
                            </Space>
                        </Panel>

                        <Panel header="Publisher" key={3}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.publishers}
                                    onChange={(event) => {
                                        setFilters({...filters, publishers: event.target.checked ? undefined : []});
                                    }}
                                >All</Checkbox>
                                {publishers.map((publisher,i) => (
                                    <Checkbox
                                        key={i}
                                        checked={!filters.publishers || filters.publishers.indexOf(publisher.id) !== -1}
                                        onChange={(event) => {
                                            if(!filters.publishers && !event.target.checked) {
                                                setFilters({...filters, publishers: publishers.map(item => item.id).filter(item => item !== publisher.id)});
                                            }else if(filters.publishers) {
                                                let filtered: any = filters.publishers ? [...filters.publishers] : [];
                                                if (event.target.checked) {
                                                    filtered = [...filtered, publisher.id]
                                                } else {
                                                    filtered = !!filters.publishers ? filters.publishers.filter(item => item !== publisher.id) : [];
                                                }
                                                if (filtered.length === publishers.length) filtered = undefined;
                                                setFilters({...filters, publishers: filtered});
                                            }
                                        }}
                                    >
                                        {formatTitleCase(publisher.title, 'Unknown Publisher')} ({publisher.count})
                                    </Checkbox>
                                ))}
                            </Space>
                        </Panel>
                    </Collapse>

                </Col>
            </Row>
        </>
    );
};

const NewReleasesFiltersDrawer = ({ filters, setFilters, weekDates}) => {
    const windowDimensions = useWindowSize();
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [newFilters, setNewFilters] = useState(filters);

    useEffect(() => {
        if(!isEqual(newFilters, filters)) {
            setNewFilters(filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const onClose = () => {
        setNewFilters(filters);
        setShowFilters(false);
    };

    return (
        <>
            <Button className="greyButton" size={"large"} onClick={() => setShowFilters(true)} >
                {windowDimensions.up(SCREEN.SM) && (<span className="filterButtonText">Filters</span>)}
                <FilterIcon className="font-12" />
            </Button>

            <IssueFilterDrawer visible={showFilters} onClose={onClose} onSearch={() => {
                setShowFilters(false);

                const fixedFilters = fixFilterObjectEmptyArrays(
                    newFilters,
                    filters,
                    ['publishers']);

                if(!isEqual(fixedFilters, filters)) {
                    setFilters(fixedFilters);
                }else if(!isEqual(fixedFilters, newFilters)) {
                    setNewFilters(fixedFilters);
                }
            }}>
                <NewReleasesFiltersContent filters={newFilters} setFilters={setNewFilters} weekDates={weekDates} />
            </IssueFilterDrawer>
        </>
    )
};

export default NewReleasesFiltersDrawer;